import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Building2, CircleCheck } from "lucide-react";

import { enableAccount, getDisabledAccounts } from "src/api/accounts";
import useHasPermissions from "src/hooks/useHasPermissions";
import CustomConfirmDialog from "src/components/CustomConfirmDialog";
import { EmptyState, PageHead } from "src/components/common";
import { showSuccessToast } from "src/lib/utils";
import type { BankAccount } from "src/types/account";

const DisabledAccounts = () => {
	const { data } = useQuery({ queryKey: ["accounts", "disabled"], queryFn: getDisabledAccounts, suspense: true });
	const accounts = data?.data ?? [];

	return (
		<div className="tw-space-y-5">
			<PageHead
				title="Disabled Bank Accounts"
				subtitle="Accounts that are no longer receiving settlements."
				actions={
					<Link
						to="/accounts"
						className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-border tw-bg-white tw-px-3 tw-py-2 tw-text-[12.5px] tw-font-semibold tw-text-text tw-transition-colors hover:tw-bg-bg"
					>
						<ArrowLeft className="tw-h-4 tw-w-4" />
						Back to Accounts
					</Link>
				}
			/>

			{accounts.length === 0 ? (
				<div className="tw-rounded-lg tw-border tw-border-border tw-bg-white tw-shadow-card">
					<EmptyState
						icon={Building2}
						title="No disabled accounts"
						description="Bank accounts you disable will be listed here and can be re-enabled."
					/>
				</div>
			) : (
				<div className="tw-grid tw-gap-4 tw-grid-cols-[repeat(auto-fill,minmax(260px,1fr))]">
					{accounts.map((account) => (
						<DisabledAccountCard account={account} key={account.uuid} />
					))}
				</div>
			)}
		</div>
	);
};

const DisabledAccountCard = ({ account }: { account: BankAccount }) => {
	const queryClient = useQueryClient();
	const canEnable = useHasPermissions("enable_bank_account");

	const { mutate: enableMutate, isLoading } = useMutation(enableAccount, {
		onSuccess: ({ message }) => {
			showSuccessToast(message || "Account enabled");
			queryClient.invalidateQueries(["accounts"]);
		},
	});

	return (
		<div className="tw-flex tw-min-h-[168px] tw-flex-col tw-rounded-lg tw-border tw-border-border tw-bg-white tw-p-5 tw-opacity-90 tw-shadow-card">
			<div className="tw-mb-4 tw-flex tw-items-start tw-justify-between tw-gap-2">
				<div className="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-[10px] tw-bg-slate-tint tw-text-slate">
					<Building2 className="tw-h-4 tw-w-4" />
				</div>
				{canEnable && (
					<CustomConfirmDialog
						trigger={
							<button
								type="button"
								disabled={isLoading}
								className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-px-2 tw-py-1 tw-text-[12.5px] tw-font-semibold tw-text-teal tw-transition-colors hover:tw-bg-teal-tint"
							>
								<CircleCheck className="tw-h-3.5 tw-w-3.5" />
								Enable
							</button>
						}
						mutateFn={() => enableMutate({ uuid: account.uuid })}
					/>
				)}
			</div>

			<div className="tw-mb-1 tw-text-[12px] tw-text-text-soft">Bank Name</div>
			<div className="tw-mb-3 tw-text-[15px] tw-font-bold tw-text-text">{account.bank_name ?? "—"}</div>

			<div className="tw-mb-1 tw-text-[12px] tw-text-text-soft">Account Number</div>
			<div className="tw-text-[15px] tw-font-bold tw-text-text">{account.account_number ?? "—"}</div>
		</div>
	);
};

export default DisabledAccounts;
